import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Protección contra pérdida de cambios al salir de la página.
// react-router 6.4 con BrowserRouter no expone useBlocker/Prompt, así que:
// - F5 / cerrar pestaña: prompt nativo del browser vía beforeunload (los navegadores
//   no permiten un modal propio ahí).
// - Botón atrás dentro de la SPA: entrada "trampa" duplicada en el history; el back
//   cae en la misma URL, se re-arma la trampa y se muestra el modal propio.
// - Links internos (menú lateral, etc.): click interceptado en fase captura antes
//   de que react-router navegue.
// `solicitarConfirmacion(continuar)` debe mostrar el modal y llamar a `continuar()`
// si el usuario decide salir (habiendo guardado o descartado antes).
export function useProteccionSalida(hayCambios, solicitarConfirmacion) {
    const navigate = useNavigate();
    const hayCambiosRef = useRef(hayCambios);
    hayCambiosRef.current = hayCambios;
    const trapArmadoRef = useRef(false);
    const saliendoRef = useRef(false);

    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (!hayCambiosRef.current || saliendoRef.current) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, []);

    useEffect(() => {
        if (hayCambios && !trapArmadoRef.current) {
            window.history.pushState(null, "", window.location.href);
            trapArmadoRef.current = true;
        }
    }, [hayCambios]);

    useEffect(() => {
        const onPopState = () => {
            if (!trapArmadoRef.current || saliendoRef.current) return;
            if (!hayCambiosRef.current) {
                // Se guardó/descartó por otro camino: dejar pasar el back real.
                trapArmadoRef.current = false;
                window.history.back();
                return;
            }
            // Re-armar la trampa y preguntar.
            window.history.pushState(null, "", window.location.href);
            solicitarConfirmacion(() => {
                saliendoRef.current = true;
                window.history.go(-2);
            });
        };
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [solicitarConfirmacion]);

    useEffect(() => {
        const onClickCapture = (e) => {
            if (!hayCambiosRef.current || saliendoRef.current) return;
            const anchor = e.target.closest?.("a[href]");
            if (!anchor) return;
            const href = anchor.getAttribute("href");
            if (!href || /^https?:\/\//.test(href) || anchor.target === "_blank") return;
            if (href === window.location.pathname) return;
            e.preventDefault();
            e.stopPropagation();
            solicitarConfirmacion(() => {
                saliendoRef.current = true;
                navigate(href);
            });
        };
        document.addEventListener("click", onClickCapture, true);
        return () => document.removeEventListener("click", onClickCapture, true);
    }, [navigate, solicitarConfirmacion]);
}
